import { useQuery } from '@tanstack/react-query';
import { axiosInstance } from '../api';
import { TBlockchain } from '../types';

interface PriceData {
  symbol: string;
  price: number;
  name: string;
  last_updated: string;
}

interface PriceResponse {
  data: PriceData;
}

// Map blockchain names to API endpoints
const getEndpointForChain = (chain: TBlockchain): string => {
  switch (chain) {
    case 'bitcoin':
      return '/pricing/btc';
    case 'ethereum':
      return '/pricing/eth';
    case 'tron':
      return '/pricing/trx';
    default:
      throw new Error(`Unsupported chain: ${chain}`);
  }
};

// Hook to get price for a specific chain
export const usePricing = (chain: TBlockchain) => {
  return useQuery<PriceData>({
    queryKey: ['price', chain],
    queryFn: async () => {
      const endpoint = getEndpointForChain(chain);
      const response = await axiosInstance.get<PriceResponse>(endpoint);
      return response.data.data;
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - matches API cache
    gcTime: 35 * 60 * 1000, // 35 minutes - keep in cache slightly longer
    refetchInterval: 30 * 60 * 1000, // Refetch every 30 minutes
    retry: 2,
  });
};

// Convenience hooks for specific chains
export const useBtcPrice = () => {
  const { data, isLoading, error } = usePricing('bitcoin');
  return {
    btcPrice: data?.price ?? 0,
    isLoading,
    error,
  };
};

export const useEthPrice = () => {
  const { data, isLoading, error } = usePricing('ethereum');
  return {
    ethPrice: data?.price ?? 0,
    isLoading,
    error,
  };
};

export const useTrxPrice = () => {
  const { data, isLoading, error } = usePricing('tron');
  return {
    trxPrice: data?.price ?? 0,
    isLoading,
    error,
  };
};

// Hook that returns the appropriate price based on chain
export const usePriceForChain = (chain: TBlockchain) => {
  const { data, isLoading, error } = usePricing(chain);
  return {
    price: data?.price ?? 0,
    symbol: data?.symbol,
    name: data?.name,
    isLoading,
    error,
  };
};
